import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, ShieldCheck, Check, X, Inbox, Clock, AlertCircle } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatApiError } from '@/utils/apiError';

/**
 * Stock Approval Inbox.
 *
 * Managers + admins see EVERY pending request — not just ones from their
 * direct reports — so cover is automatic when someone's off. Team users land
 * on this same page but get the "Mine" tab as default and can only cancel
 * their own pending submissions.
 */
export default function StockApprovals() {
  const navigate = useNavigate();
  const { isStaff, user } = useContext(AuthContext);
  const isApprover = user && (user.role === 'admin' || user.role === 'manager');
  const isTeam = user?.role === 'team';
  const confirm = useConfirm();

  const [tab, setTab] = useState(isApprover ? 'pending' : 'mine');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/approvals?status=${tab}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load approvals');
      setRows(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    load();
  }, [isStaff, navigate, load]);

  const approve = async (row) => {
    const ok = await confirm({
      title: 'Approve this request?',
      body: `This will apply the action to live stock immediately.\n\n${row.summary}`,
      confirmLabel: 'Approve',
      tone: 'primary',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/approvals/${row.id}/approve`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to approve');
      toast.success('Approved & applied.');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const reject = async (row) => {
    const reason = window.prompt(`Reject "${row.summary}"?\n\nOptional reason (will be emailed to ${row.submitted_by_name}):`, '');
    if (reason === null) return; // user clicked cancel
    try {
      const res = await fetch(`${API_URL}/api/stock/approvals/${row.id}/reject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to reject');
      toast.success('Request rejected.');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const cancelOwn = async (row) => {
    const ok = await confirm({
      title: 'Cancel this request?',
      body: `Withdraw your "${row.summary}" submission. You can submit again afterwards.`,
      confirmLabel: 'Withdraw',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/approvals/${row.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to cancel');
      toast.success('Withdrawn.');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const tabs = useMemo(() => {
    const out = [];
    if (isApprover) {
      out.push({ key: 'pending', label: 'Pending', icon: Inbox });
      out.push({ key: 'approved', label: 'Recently approved', icon: Check });
      out.push({ key: 'rejected', label: 'Recently rejected', icon: X });
    }
    out.push({ key: 'mine', label: isApprover ? 'My submissions' : 'My pending', icon: Clock });
    return out;
  }, [isApprover]);

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff/stock')} data-testid="approvals-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} />
              Stock Approvals
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">
              {isApprover ? 'Review & action team-member stock requests' : 'Track your submitted stock requests'}
            </p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Staff">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4">
        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`approvals-tab-${t.key}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${active ? 'bg-[#1f4030] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <Card className="p-10 text-center text-gray-500">
            <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="italic">
              {tab === 'pending' && 'Nothing waiting for approval right now.'}
              {tab === 'approved' && 'No recently approved requests.'}
              {tab === 'rejected' && 'No recently rejected requests.'}
              {tab === 'mine' && 'You haven\u2019t submitted any stock requests yet.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {rows.map(row => (
              <ApprovalCard
                key={row.id}
                row={row}
                isApprover={isApprover}
                isTeam={isTeam}
                tab={tab}
                onApprove={approve}
                onReject={reject}
                onCancel={cancelOwn}
                currentUserId={user?.user_id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ApprovalCard({ row, isApprover, isTeam, tab, onApprove, onReject, onCancel, currentUserId }) {
  const isPending = row.status === 'pending';
  const isMine = row.submitted_by === currentUserId;
  const accentByAction = {
    delivery: 'bg-blue-100 text-blue-800 border-blue-200',
    order: 'bg-rose-100 text-rose-800 border-rose-200',
    sell: 'bg-rose-100 text-rose-800 border-rose-200',
    packing_manual: 'bg-amber-100 text-amber-800 border-amber-200',
    adjust: 'bg-amber-100 text-amber-800 border-amber-200',
    count: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    discrepancy: 'bg-orange-100 text-orange-800 border-orange-200',
  }[row.action] || 'bg-gray-100 text-gray-700 border-gray-200';
  const submittedAt = new Date(row.submitted_at);
  const decidedAt = row.decided_at ? new Date(row.decided_at) : null;
  return (
    <Card className="p-3 sm:p-4" data-testid={`approval-row-${row.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${accentByAction}`}>
              {row.action === 'packing_manual' ? 'packing order' : row.action}
            </span>
            <span className="text-xs text-gray-500">
              by <strong className="text-gray-700">{row.submitted_by_name}</strong>
              {row.reports_to_name && <span className="text-gray-400"> → {row.reports_to_name}</span>}
            </span>
            <span className="text-xs text-gray-400">· {submittedAt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <p className="text-sm font-semibold text-gray-900 break-words">{row.summary}</p>
          {row.payload?.notes && (
            <p className="text-xs text-gray-500 mt-1 italic">&ldquo;{row.payload.notes}&rdquo;</p>
          )}
          {row.status === 'rejected' && row.rejection_reason && (
            <p className="text-xs text-rose-700 mt-1 flex items-start gap-1"><AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {row.rejection_reason}</p>
          )}
          {decidedAt && (
            <p className="text-[11px] text-gray-400 mt-1">
              {row.status === 'approved' ? 'Approved' : 'Rejected'} by {row.decided_by_name} · {decidedAt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        {/* Action buttons */}
        {isPending && isApprover && (
          <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
            <Button
              size="sm"
              onClick={() => onApprove(row)}
              data-testid={`approval-approve-${row.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Approve</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onReject(row)}
              data-testid={`approval-reject-${row.id}`}
              className="border-rose-200 text-rose-700 hover:bg-rose-50"
            >
              <X className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Reject</span>
            </Button>
          </div>
        )}
        {isPending && isTeam && isMine && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCancel(row)}
            data-testid={`approval-cancel-${row.id}`}
            className="border-gray-300 text-gray-700 hover:bg-gray-50 shrink-0"
          >
            <X className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Withdraw</span>
          </Button>
        )}
        {!isPending && (
          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded shrink-0 ${row.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
            {row.status}
          </span>
        )}
      </div>
    </Card>
  );
}
